import { Button } from "@svea/design-system-components-react";
import {
	AriaPopover,
	ButtonGroup,
	Condition,
	FormHelperText,
	Grid,
	Heading,
	isRequired,
	Markdown,
	P,
	ReactSelectField,
	Stack,
	TextAreaField,
	useText,
	validateMaxLength,
} from "@sveabank/cc-common";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { usePageContext } from "../../../../hooks/context";
import type {
	ClcCompanyStructureInput,
	ClcCompanyStructureSignatureCombination,
	ClcRepresentative,
	RoleValue,
} from "../../../../types/clc-application";
import { useClcCompanyApplication } from "../../hooks";
import { AddRepresentativeModal } from "./add-representative-modal";
import RepresentativeField from "./representative-field";

type FormProps = {
	representatives: ClcRepresentative[];
	signatoryCombination: ClcCompanyStructureSignatureCombination;
};

type Props = {
	onSubmit: (data: ClcCompanyStructureInput) => Promise<void>;
	onCancel: () => void;
};

const isValidBoard = (allRoles: Array<RoleValue | null>) => {
	if (allRoles.filter((role) => role === "BoardMember").length >= 3) return true;

	if (
		allRoles.some((role) => role === "BoardMember") &&
		allRoles.some((role) => role === "AlternateBoardMember")
	)
		return true;

	return false;
};

const ClcCompanyStructure = ({ onSubmit, onCancel }: Props) => {
	const {
		signatoryCombinations,
		isPostingClcRepresentatives,
		clcApplicationDetails: {
			applicant: { nationalId: applicantNationalId },
			representatives: { value: companyStructure },
			companyShares: { value: shareholdersValue },
			beneficialOwners: { value: beneficialOwnersValue },
		},
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();

	const [isOpen, setIsOpen] = useState(false);

	const currentRepresentatives = companyStructure?.representatives || [];

	const initialValues = useMemo(() => {
		return {
			representatives: [
				...currentRepresentatives,
				...(shareholdersValue?.shareholders
					.map((shareholder) => ({
						firstName: shareholder.firstName,
						lastName: shareholder.lastName,
						nationalId: shareholder.nationalId,
						postCode: shareholder.postCode,
						postalAddress: shareholder.postalAddress,
						town: shareholder.town,
						functions: (shareholder.nationalId === applicantNationalId
							? ["BoardMember"]
							: []) satisfies RoleValue[], // applicant must automatically be a board member
					}))
					.filter(
						(shareholder) =>
							!currentRepresentatives.find((r) => r.nationalId === shareholder.nationalId),
					) || []),
			] satisfies Array<ClcRepresentative>,
			signatoryCombination: companyStructure?.signatoryCombination,
		};
	}, [shareholdersValue]);

	const submitForm = (values: FormProps) => {
		const allRoles = values.representatives.reduce<Array<RoleValue | null>>((acc, curr) => {
			if (curr.functions) acc.push(...curr.functions);
			return acc;
		}, []);

		if (!isValidBoard(allRoles)) {
			return { [FORM_ERROR]: t(texts.newCompany.companyStructure.incompleteBoardMembers) };
		}

		if (!values.signatoryCombination.signatoryCombination)
			return { [FORM_ERROR]: t(texts.newCompany.companyStructure.missingSignatoryCombination) };

		const signatoryCombination: ClcCompanyStructureSignatureCombination =
			values.signatoryCombination.signatoryCombination === "Other"
				? {
						details: values.signatoryCombination.details,
						signatoryCombination: "Other",
					}
				: {
						details: null,
						signatoryCombination: values.signatoryCombination.signatoryCombination,
					};

		const submitValues: ClcCompanyStructureInput = {
			signatoryCombination,
			representatives: values.representatives.map((r) => ({
				functions: r.functions?.filter((f) => f !== undefined) || [],
				nationalId: r.nationalId,
			})),
		};

		onSubmit(submitValues);
	};

	if (!shareholdersValue || !beneficialOwnersValue) return null;

	return (
		<Grid>
			<Grid.Col
				span={{
					base: 12,
					tablet: 8,
				}}
			>
				<Stack gap={4}>
					<Stack gap={2}>
						<Stack direction="row" gap={1}>
							<Heading.H3 size="h4" italic={false}>
								{t(texts.newCompany.companyStructure.representativesTitle)}
							</Heading.H3>

							<AriaPopover
								variant="info"
								content={() => (
									<Markdown>{t(texts.newCompany.companyStructure.helpText1)}</Markdown>
								)}
							/>
						</Stack>
						<P>{t(texts.newCompany.companyStructure.bodyText1)}</P>
						<P>{t(texts.newCompany.companyStructure.bodyText2)}</P>
					</Stack>
					<Form<FormProps>
						mutators={{
							...arrayMutators,
						}}
						initialValues={initialValues}
						onSubmit={submitForm}
						render={({
							values,
							submitError,
							handleSubmit,
							form: {
								mutators: { push },
							},
						}) => {
							return (
								<form onSubmit={handleSubmit}>
									<Stack gap={4}>
										<FieldArray<ClcRepresentative> name="representatives">
											{({ fields }) => {
												return (
													<>
														{fields.map((name, index) => {
															const representative = fields.value[index];
															if (!representative) throw new Error("No representative found");
															return (
																<RepresentativeField
																	key={name}
																	name={name}
																	representative={representative}
																	shareholders={shareholdersValue.shareholders}
																	onRemovePerson={() => {
																		fields.remove(index);
																	}}
																/>
															);
														})}
													</>
												);
											}}
										</FieldArray>
										<Stack>
											<ButtonGroup row align="start">
												<Button
													variant="plain"
													iconStart="plusCircle"
													onPress={() => setIsOpen(true)}
												>
													{t(texts.newCompany.companyStructure.addRepresentativeModalHeading)}
												</Button>
											</ButtonGroup>
										</Stack>
										<Stack gap={2}>
											<Stack direction="row" gap={1}>
												<Heading.H3 size="h4" italic={false}>
													{t(texts.newCompany.companyStructure.signatoryCombinationTitle)}
												</Heading.H3>
												<AriaPopover
													variant="info"
													content={() => (
														<Markdown>{t(texts.newCompany.companyStructure.helpText2)}</Markdown>
													)}
												/>
											</Stack>

											<ReactSelectField
												label={t(texts.newCompany.companyStructure.chooseSignatoryCombinationLabel)}
												floatingLabel
												name="signatoryCombination.signatoryCombination" // Name chosen to match backend structure
												options={signatoryCombinations.map((signatoryCombination) => ({
													label:
														signatoryCombination.labels.find((label) => label.language === "sv")
															?.label || "",
													value: signatoryCombination.value,
												}))}
												placeholder={"Välj..."} // TODO: Replace once correct label is determined
												validators={[isRequired]}
											/>
											<Condition when="signatoryCombination.signatoryCombination" is="Other">
												<TextAreaField
													validators={[isRequired, validateMaxLength(1000)]}
													name="signatoryCombination.details"
													label={t(
														texts.newCompany.companyStructure.signatoryCombinationOtherLabel,
													)}
													maxLength={1000}
												/>
											</Condition>
										</Stack>
										<ButtonGroup row align="start">
											<Button type="button" variant="secondary" onPress={onCancel}>
												{t(texts.common.abort)}
											</Button>
											<Button
												type="submit"
												isPending={isPostingClcRepresentatives}
												data-tracking-action="CLC Application Continue"
												data-tracking-name="Company Structure"
											>
												{t(texts.common.continue)}
											</Button>
										</ButtonGroup>
										<FormHelperText error>{submitError}</FormHelperText>
									</Stack>

									<AddRepresentativeModal
										open={isOpen}
										setOpen={setIsOpen}
										addRepresentative={(r) =>
											push?.("representatives", {
												...r,
												functions: [],
											})
										}
										addedNationalIds={values.representatives.map((r) => r.nationalId)}
									/>
								</form>
							);
						}}
					/>
				</Stack>
			</Grid.Col>
		</Grid>
	);
};

export default ClcCompanyStructure;
